import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle2, Clock, Mail, ArrowRight } from "lucide-react";
import { isUaeMode } from "../utils/uaeLocale";

const PendingApproval = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const uaeMode = isUaeMode();

  const organizationName = location.state?.organizationName || 'Your organization';
  const organizationSlug = location.state?.organizationSlug || null;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: '#f8f9fa' }}>
      {/* Subtle confetti */}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-110vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(540deg); opacity: 0; }
        }
        .bf-confetti { position: fixed; top: -20px; width: 8px; height: 8px; border-radius: 2px; pointer-events: none; }
      `}</style>
      {[...Array(36)].map((_, i) => (
        <div
          key={i}
          className="bf-confetti"
          style={{
            left: `${Math.random() * 100}%`,
            background: ['#2563eb', '#4f46e5', '#10b981', '#f59e0b'][i % 4],
            animation: `confetti-fall ${2.4 + Math.random() * 2}s linear ${Math.random() * 1.5}s forwards`,
          }}
        />
      ))}

      <Card className="w-full max-w-lg shadow-xl relative rounded-2xl" data-testid="pending-approval-card">
        <CardHeader className="text-center pt-12 pb-4">
          <div className="flex justify-center mb-5">
            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-blue-50 border border-blue-100">
              <CheckCircle2 className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold mb-3" style={{ color: '#1a1a1a' }}>
            You're on the list
            {uaeMode && <span dir="rtl" lang="ar" className="block text-2xl mt-1.5 text-gray-800">أنت على القائمة</span>}
          </CardTitle>
          <p className="text-gray-600 text-base leading-relaxed px-2">
            Thanks for signing up <span className="font-semibold text-gray-900">{organizationName}</span>. Your
            application has been received — <span className="font-semibold text-gray-900">no action needed on your end.</span>
          </p>
          {uaeMode && (
            <p dir="rtl" lang="ar" className="text-gray-600 text-sm leading-relaxed px-2 mt-2" data-testid="pending-intro-ar">
              شكراً لتسجيل <span className="font-semibold text-gray-900">{organizationName}</span>. تم استلام طلبك — لا حاجة لأي إجراء من طرفك.
            </p>
          )}
        </CardHeader>

        <CardContent className="px-8 pb-8">
          {/* Confirmation email note */}
          <div className="flex items-start gap-3 bg-blue-50/60 border border-blue-100 rounded-xl p-4 mb-6">
            <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700 leading-relaxed">
              We've emailed you a confirmation. Keep an eye on your inbox — we'll email you again the moment your
              account is approved.
              {uaeMode && (
                <span dir="rtl" lang="ar" className="block mt-1.5 text-gray-600">
                  أرسلنا لك رسالة تأكيد عبر البريد الإلكتروني — وسنراسلك مجدداً فور الموافقة على حسابك.
                </span>
              )}
            </p>
          </div>

          {/* What happens next */}
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-400 mb-3">
            What happens next{uaeMode && <span dir="rtl" lang="ar" className="normal-case font-semibold ml-1.5">· ماذا يحدث الآن؟</span>}
          </p>
          <ol className="space-y-3 mb-6">
            {[
              { en: "Our team reviews your account (usually within 1 business day).", ar: "يراجع فريقنا حسابك (عادةً خلال يوم عمل واحد)." },
              { en: "We approve your account and set you up.", ar: "نوافق على حسابك ونجهّز كل شيء لك." },
              { en: "You get an email to log in and start taking bookings.", ar: "تصلك رسالة لتسجيل الدخول والبدء باستقبال الحجوزات." },
            ].map((stepText, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold flex items-center justify-center mt-0.5">
                  {idx + 1}
                </span>
                <span className="text-sm text-gray-700 leading-relaxed">
                  {stepText.en}
                  {uaeMode && <span dir="rtl" lang="ar" className="block text-gray-500 mt-0.5">{stepText.ar}</span>}
                </span>
              </li>
            ))}
          </ol>

          {/* Org info */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Your organization{uaeMode && <span dir="rtl" lang="ar" className="ml-1.5">· منشأتك</span>}</p>
            <p className="text-lg font-bold text-gray-900">{organizationName}</p>
            {organizationSlug && (
              <p className="text-sm text-gray-600 mt-1">
                Shortname: <span className="font-mono font-semibold text-gray-900">{organizationSlug}</span>
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500 justify-center mb-5">
            <Clock className="w-4 h-4" />
            {uaeMode ? <span>Typical approval time: within 1 business day <span dir="rtl" lang="ar">· عادةً خلال يوم عمل واحد</span></span> : 'Typical approval time: within 1 business day'}
          </div>

          <Button
            onClick={() => navigate('/')}
            className="w-full h-12 text-base font-semibold !bg-blue-600 hover:!bg-blue-700 text-white"
            data-testid="pending-back-home"
          >
            {uaeMode ? 'Back to home · العودة للرئيسية' : 'Back to home'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center rounded-b-2xl">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} BookingFlows. All rights reserved.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default PendingApproval;
